import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'

export interface FolderWatch {
  id: string
  name: string
  provider: 's3' | 'dropbox' | 'google_drive' | 'sftp'
  poll_interval: number
  is_active: boolean
  last_polled_at: string | null
  created_at: string
}

export interface WatchFile {
  file_path: string
  file_name: string
  file_size: number | null
  remote_modified_at: string | null
  last_seen_at: string | null
  last_ingested_at: string | null
  ingestion_job_id: string | null
  job_status: string | null
}

export interface CreateFolderWatchPayload {
  name: string
  provider: string
  config: Record<string, unknown>
  poll_interval: number
}

export function useFolderWatches() {
  return useQuery<FolderWatch[]>({
    queryKey: ['folder-watches'],
    queryFn: () => api.get<FolderWatch[]>('/api/v1/folder-watches'),
  })
}

export function useWatchFiles(watchId: string | null) {
  return useQuery<WatchFile[]>({
    queryKey: ['folder-watch-files', watchId],
    queryFn: () => api.get<WatchFile[]>(`/api/v1/folder-watches/${watchId}/files`),
    enabled: !!watchId,
  })
}

export function useCreateFolderWatch() {
  const qc = useQueryClient()
  return useMutation<FolderWatch, Error, CreateFolderWatchPayload>({
    mutationFn: (payload) => api.post<FolderWatch>('/api/v1/folder-watches', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['folder-watches'] }),
  })
}

export function useToggleFolderWatch() {
  const qc = useQueryClient()
  return useMutation<FolderWatch, Error, { id: string; is_active: boolean }>({
    mutationFn: ({ id, is_active }) =>
      api.patch<FolderWatch>(`/api/v1/folder-watches/${id}`, { is_active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['folder-watches'] }),
  })
}

export function useDeleteFolderWatch() {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (id) => api.delete<void>(`/api/v1/folder-watches/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['folder-watches'] }),
  })
}

export function useTriggerPoll() {
  const qc = useQueryClient()
  return useMutation<{ message: string; task_id: string }, Error, string>({
    mutationFn: (id) =>
      api.post<{ message: string; task_id: string }>(`/api/v1/folder-watches/${id}/poll`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['folder-watches'] }),
  })
}
